/**
 * printPrefs — 인쇄 미리보기 설정의 마지막 상태를 브라우저(localStorage)에 기억한다. 기기별이다.
 *
 * 저장 대상은 화이트리스트다 — 새 상태가 생겨도 저절로 새어 나가지 않는다.
 * 일부러 뺀 것:
 *   includeSolutions / includeAnswers — 켜진 채 기억되면 배부용 시험지에 풀이·정답이 딸려
 *     인쇄된다. 인쇄는 되돌릴 수 없고 다시 켜는 비용은 클릭 한 번이라 매번 명시적으로 켜게 둔다.
 *   patOverrides(면별 패턴 지정) — 키가 "면 인덱스"라 다른 시험지에서는 엉뚱한 면에 붙는다.
 *     문항 수가 같은 다른 시험지는 걸러낼 방법이 없다. 프리셋(patPreset)은 시험지와 무관하므로 저장한다.
 *
 * 초기화 버튼은 저장값이 아니라 아래 PRINT_DEFAULTS(초기셋)로 되돌린다 — 둘은 별개다.
 * 되돌린 결과는 다시 저장되므로 "초기화했는데 다음에 열면 옛 값이 돌아오는" 일은 없다.
 */
import { PAPERS_V4 } from './printSpec';
import { DEFAULT_PRESET, PRESETS, presetKeysFor } from './pagePatterns';
import { B4_MARKER_LABEL_MAX_LENGTH, uniqueB4MarkerNumbers } from './b4Markers';

// 비호환 저장 형태로 바뀌면 v2로 올린다. 선택 필드 추가는 기존 저장값에 기본값을 채운다.
const STORAGE_KEY = 'pv4-print-prefs:v1';

/**
 * 초기셋 — 초기화 버튼이 되돌릴 목적지이자 저장값이 없을 때의 기본값.
 * 얼려 둔다: 여기에 직접 대입하면(예: patPreset.B4 = …) 초기화의 목적지가 조용히 오염된다.
 * 얼려 두면 그 순간 TypeError로 드러난다(ES 모듈은 항상 strict).
 */
export const PRINT_DEFAULTS = Object.freeze({
  paperKey: 'A4',
  zoom: 0.55,
  b4Line1: '2027학년도 SKYF모의고사',
  b4Title: '수학 영역',
  b4Subject: '',
  b4Line2: '',
  // 표지(수능형 앞장) — 문제 1면 앞에 한 장 더 붙는다. 번호는 붙지 않는다(수능 관례).
  // 수능형(B4)에서는 표지가 있는 것이 기본 모습이라 켜 둔다(오너 지시 2026-08-31).
  b4Cover: true,
  // 필적 확인란 문구 — 회색 박스 안 한 줄. 학원마다 바꾸는 자리다.
  b4CoverPhrase: '꿈은 꾸는 것이 아닌 이루는 것이다.',
  b4Marker5: '1', // 수능 기준: 1~15가 5지선다형
  b4MarkerShort: '16', // 수능 기준: 16부터 단답형
  b4MarkerCustom: 'none',
  b4Marker5Label: '5지선다형',
  b4MarkerShortLabel: '단답형',
  b4MarkerCustomLabel: '서술형',
  patPreset: Object.freeze({ ...DEFAULT_PRESET }),
});

// 초기셋의 사본. patPreset은 상태로 들어가 갱신되므로 반드시 새 객체로 넘긴다.
export const freshPrintDefaults = () => ({ ...PRINT_DEFAULTS, patPreset: { ...PRINT_DEFAULTS.patPreset } });

const asString = (v, fallback) => (typeof v === 'string' ? v : fallback);
const asMarkerLabel = (v, fallback) => asString(v, fallback).slice(0, B4_MARKER_LABEL_MAX_LENGTH);
// 마커 값은 'none' 또는 문제 번호 문자열. 문항 수 초과 여부는 화면에서 따로 클램프한다.
const asMarker = (v, fallback) => (v === 'none' || /^[1-9]\d{0,2}$/.test(String(v)) ? String(v) : fallback);

/**
 * 저장값을 읽어 형태를 검증한다. 없거나 깨졌으면 초기셋.
 * localStorage는 사파리 프라이빗 모드·용량 초과에서 throw하므로 전부 감싼다.
 */
export function loadPrintPrefs() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return freshPrintDefaults();
  }
  if (!raw) return freshPrintDefaults();

  let saved;
  try {
    saved = JSON.parse(raw);
  } catch (e) {
    return freshPrintDefaults();
  }
  return normalizePrintPrefs(saved);
}

// 시험지별 임시 설정도 같은 문구 길이·마커·용지 검증을 사용한다.
export function normalizePrintPrefs(saved) {
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return freshPrintDefaults();

  // 프리셋은 용지마다 고를 수 있는 목록이 다르다(수능 표준은 B4 전용) — 목록 밖이면 그 용지의 기본값
  const patPreset = { ...PRINT_DEFAULTS.patPreset };
  Object.keys(PAPERS_V4).forEach((paper) => {
    const key = saved.patPreset && saved.patPreset[paper];
    if (typeof key === 'string' && PRESETS[key] && presetKeysFor(paper).includes(key)) patPreset[paper] = key;
  });

  const zoom = Number(saved.zoom);
  const markerNumbers = uniqueB4MarkerNumbers({
    b4Marker5: asMarker(saved.b4Marker5, PRINT_DEFAULTS.b4Marker5),
    b4MarkerShort: asMarker(saved.b4MarkerShort, PRINT_DEFAULTS.b4MarkerShort),
    b4MarkerCustom: asMarker(saved.b4MarkerCustom, PRINT_DEFAULTS.b4MarkerCustom),
  });
  return {
    paperKey: PAPERS_V4[saved.paperKey] ? saved.paperKey : PRINT_DEFAULTS.paperKey,
    zoom: Number.isFinite(zoom) && zoom >= 0.2 && zoom <= 1.2 ? zoom : PRINT_DEFAULTS.zoom,
    b4Line1: asString(saved.b4Line1, PRINT_DEFAULTS.b4Line1),
    b4Title: asString(saved.b4Title, PRINT_DEFAULTS.b4Title),
    b4Subject: asString(saved.b4Subject, PRINT_DEFAULTS.b4Subject),
    b4Line2: asString(saved.b4Line2, PRINT_DEFAULTS.b4Line2),
    b4Cover: typeof saved.b4Cover === 'boolean' ? saved.b4Cover : PRINT_DEFAULTS.b4Cover,
    b4CoverPhrase: asString(saved.b4CoverPhrase, PRINT_DEFAULTS.b4CoverPhrase),
    ...markerNumbers,
    b4Marker5Label: asMarkerLabel(saved.b4Marker5Label, PRINT_DEFAULTS.b4Marker5Label),
    b4MarkerShortLabel: asMarkerLabel(saved.b4MarkerShortLabel, PRINT_DEFAULTS.b4MarkerShortLabel),
    b4MarkerCustomLabel: asMarkerLabel(saved.b4MarkerCustomLabel, PRINT_DEFAULTS.b4MarkerCustomLabel),
    patPreset,
  };
}

/** PRINT_DEFAULTS에 있는 키만 저장한다(화이트리스트). 저장 실패는 기능에 영향이 없으므로 무시. */
export function savePrintPrefs(state) {
  try {
    const out = {};
    Object.keys(PRINT_DEFAULTS).forEach((k) => { out[k] = state[k]; });
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch (e) {
    /* 저장 불가(프라이빗 모드·용량 초과) — 이번 세션에서만 유지된다 */
  }
}
